<template>
    <div v-if="model">
        <b-row v-if="!executeQuery.show">
            <b-col cols="4">
                <b-row>
                    <b-col cols="12">
                        <b-card class="modern-card">
                            <b-card-header class="modern-header">
                                <h5 class="mb-0">
                                    <b-icon icon="gear" class="mr-2"></b-icon>
                                    {{ $t('connectionSettings') }}
                                </h5>
                            </b-card-header>

                            <b-card-body class="modern-body">
                                <b-form-group
                                    :label="$t('id')"
                                    :description="$t('ratchetIdDescription')"
                                    label-class="form-label"
                                >
                                    <b-form-input v-model="connectionId" disabled class="modern-input"></b-form-input>
                                </b-form-group>
                                <b-form-group
                                    :label="$t('type')"
                                    label-class="form-label"
                                >
                                    <div class="db-type-display">
                                        <img :src="getIcon(model.type)" class="db-icon" />
                                        <span class="db-name">{{ model.type }}</span>
                                    </div>
                                </b-form-group>
                                <div class="credentials-section" v-if="model.credentials">
                                    <div class="d-flex justify-content-between align-items-center mb-3">
                                        <h5 class="section-subtitle mb-0">{{ $t('credentials') }}</h5>
                                        <b-button 
                                            size="sm" 
                                            :variant="editCredentials ? 'outline-secondary' : 'outline-primary'" 
                                            @click="editCredentials = !editCredentials"
                                        >
                                            <b-icon :icon="editCredentials ? 'eye-slash' : 'pencil'" class="mr-1"></b-icon>
                                            {{ editCredentials ? 'Hide' : 'Edit' }}
                                        </b-button>
                                    </div>
                                    <b-form-group
                                        label="Credential Type"
                                        label-class="form-label"
                                    >
                                        <div class="credential-type-badge">{{ model.credentials.type }}</div>
                                    </b-form-group>
                                </div>

                                <template v-if="editCredentials && model.credentials && model.credentials.type == 'DSN'">
                                    <b-form-group
                                        label="DSN"
                                        label-class="form-label"
                                        description="Database connection string"
                                    >
                                        <b-form-input type="password" v-model="credentialValues.dsn" class="modern-input" placeholder="Enter new DSN to update"/>
                                    </b-form-group>
                                </template>

                                <template v-if="editCredentials && model.credentials && model.credentials.type == 'Credentials'">
                                    <b-row>
                                        <b-col cols="12" md="6">
                                            <b-form-group
                                                label="User"
                                                label-class="form-label"
                                            >
                                                <b-form-input v-model="credentialValues.user" class="modern-input" placeholder="Enter new user to update"/>
                                            </b-form-group>
                                        </b-col>
                                        <b-col cols="12" md="6">
                                            <b-form-group
                                                label="Password"
                                                label-class="form-label"
                                            >
                                                <b-form-input type="password" v-model="credentialValues.password" class="modern-input" placeholder="Enter new password to update"/>
                                            </b-form-group>
                                        </b-col>
                                    </b-row>

                                    <b-row>
                                        <b-col cols="12" md="6">
                                            <b-form-group
                                                label="Host"
                                                label-class="form-label"
                                            >
                                                <b-form-input v-model="credentialValues.host" class="modern-input" placeholder="Enter new host to update"/>
                                            </b-form-group>
                                        </b-col>
                                        <b-col cols="12" md="6">
                                            <b-form-group
                                                label="Database Name"
                                                label-class="form-label"
                                            >
                                                <b-form-input v-model="credentialValues.dbname" class="modern-input" placeholder="Enter new database name to update"/>
                                            </b-form-group>
                                        </b-col>
                                    </b-row>
                                </template>

                                <template v-if="editCredentials && model.credentials && model.credentials.type == 'Service Account JSON'">
                                    <b-form-group
                                        label="Service Account JSON"
                                        label-class="form-label"
                                        description="Paste the entire JSON key file content from Google Cloud Console"
                                    >
                                        <b-form-textarea 
                                            v-model="credentialValues.service_account_json" 
                                            rows="10"
                                            class="modern-textarea"
                                            placeholder='{"type": "service_account", "project_id": "...", ...}'
                                        />
                                    </b-form-group>
                                </template>
                            </b-card-body>

                            <b-card-footer class="modern-footer">
                                <div class="d-flex align-items-center">
                                    <b-button variant="success" class="modern-btn mr-auto" @click="save">
                                        <b-icon icon="check-circle" class="mr-2"></b-icon>
                                        {{ $t('save') }}
                                    </b-button>
                                    <b-dropdown variant="outline-secondary" class="modern-btn" right no-caret>
                                        <template #button-content>
                                            <b-icon icon="three-dots-vertical"></b-icon>
                                        </template>
                                        <b-dropdown-item @click="testConnection">
                                            <b-icon icon="lightning" class="mr-2"></b-icon>
                                            {{ $t('testConnection') }}
                                        </b-dropdown-item>
                                        <b-dropdown-divider></b-dropdown-divider>
                                        <b-dropdown-item variant="danger" @click="remove">
                                            <b-icon icon="trash" class="mr-2"></b-icon>
                                            {{ $t('remove') }}
                                        </b-dropdown-item>
                                    </b-dropdown>
                                </div>
                            </b-card-footer>
                        </b-card>  
                    </b-col>
                </b-row>
            </b-col>
            <b-col cols="8">
                <b-row>
                     <b-col cols="12" class="d-flex align-items-start mb-3">
                        <b-button variant="primary" size="lg" class="modern-btn mr-2" @click="newQuery">
                            <b-icon icon="plus-circle" class="mr-2"></b-icon>
                            {{ $t('newQuery') }}
                        </b-button>
                        <b-form-input 
                            v-model="searchQuery" 
                            placeholder="Search queries..." 
                            class="modern-input"
                            style="max-width: 400px;"
                        >
                            <template #prepend>
                                <b-icon icon="search"></b-icon>
                            </template>
                        </b-form-input>
                    </b-col>

                    <b-col cols="12" md="6" v-for="query in filteredQueries" :key="query.id">
                        <b-card class="query-card">
                            <div class="query-name">{{ query.id }}</div>
                            <div class="query-actions">
                                <b-button size="sm" variant="outline-primary" class="action-btn" @click="openEditModal(query)">
                                    <b-icon icon="pencil"></b-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-success" class="action-btn" @click="tryQuery(query)">
                                    <b-icon icon="play"></b-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-danger" class="action-btn" @click="removeQuery(query)">
                                    <b-icon icon="trash"></b-icon>
                                </b-button>
                            </div>
                        </b-card>
                    </b-col>
                </b-row>
            </b-col>
           
        </b-row>

        <b-row v-if="executeQuery.show">
            <template v-if="model.type == 'MySQL' || model.type == 'PostgreSQL'">
                <b-col cols="12"><b-icon icon="arrow-left" @click="executeQuery.show = false"/></b-col>
                <b-col cols="12" class="mt-4"><b-button variant="success"><b-icon icon="play" @click="runQuery"/></b-button></b-col>
                <b-col cols="12" class="mt-4"><CodeEditor v-model="executeQuery.query" :withButton="false" :options="{mode: 'sql'}" /></b-col>
                <b-col cols="12">
                    <ol>
                        <li v-for="(param, index) in executeQuery.params" :key="index" class="mb-2">
                            <b-row>
                                <b-col cols="8">
                                    <b-input v-model="executeQuery.params[index]" />
                                </b-col>
                                <b-col cols="2">
                                    <b-icon icon="trash" class="action" @click="removeParam(index)"/>
                                </b-col>
                            </b-row>
                        </li>
                    </ol>
                    <b-button @click="addParam">{{ $t('addParam') }}</b-button>
                    <b-card v-if="executeQuery.results" class="mt-4">
                        <b-table dark striped hover responsive :items="executeQuery.results"  />
                    </b-card>
                </b-col>
            </template>

            <template v-if="model.type == 'MongoDB'">
                <b-col cols="12"><b-icon icon="arrow-left" @click="executeQuery.show = false"/></b-col>
                <b-col cols="12" class="mt-4"><b-button variant="success"><b-icon icon="play" @click="runQuery"/></b-button></b-col>
                <b-col cols="12" class="mt-4"><CodeEditor v-model="executeQuery.query" :withButton="false" /></b-col>
                <b-col cols="12">
                    <b-col cols="12"><small><a href="https://www.mongodb.com/docs/manual/reference/command" target="_blank">MongoDB command documentation</a></small></b-col>
                    <b-card v-if="executeQuery.results" class="mt-4">
                        <b-table dark striped hover responsive :items="executeQuery.results"  />
                    </b-card>
                </b-col>
            </template>

            <template v-if="model.type == 'BigQuery'">
                <b-col cols="12"><b-icon icon="arrow-left" @click="executeQuery.show = false"/></b-col>
                <b-col cols="12" class="mt-4"><b-button variant="success" @click="runQuery"><b-icon icon="play"/> Run Query</b-button></b-col>
                <b-col cols="12" class="mt-4"><CodeEditor v-model="executeQuery.query" :withButton="false" :options="{mode: 'sql'}" /></b-col>
                <b-col cols="12">
                    <h5 class="mt-3 mb-3">Named Parameters</h5>
                    <div v-for="(param, key) in executeQuery.namedParams" :key="key" class="mb-2">
                        <b-row>
                            <b-col cols="4">
                                <b-input :value="key" @input="updateParamKey(key, $event)" placeholder="Parameter name (e.g., user_id)" />
                            </b-col>
                            <b-col cols="6">
                                <b-input v-model="executeQuery.namedParams[key]" placeholder="Parameter value" />
                            </b-col>
                            <b-col cols="2">
                                <b-icon icon="trash" class="action" @click="removeNamedParam(key)"/>
                            </b-col>
                        </b-row>
                    </div>
                    <b-button @click="addNamedParam" class="mt-2">Add Named Parameter</b-button>
                    <b-col cols="12" class="mt-3"><small><a href="https://cloud.google.com/bigquery/docs/reference/standard-sql/query-syntax#query-parameters" target="_blank">BigQuery named parameters reference</a></small></b-col>
                    <b-card v-if="executeQuery.results" class="mt-4">
                        <b-table dark striped hover responsive :items="executeQuery.results"  />
                    </b-card>
                </b-col>
            </template>
        </b-row>

        <!-- Edit Query Modal -->
        <main-dialog v-model="editModal.show" :title="'Edit Query: ' + editModal.query.id" size="xl" @ok="handleEditModalOk">
            <CodeEditor v-model="editModal.query.expression" :withButton="false"/>
        </main-dialog>

        <!-- Create Query Modal -->
        <main-dialog v-model="createModal.show" title="Create New Query" size="xl" @ok="handleCreateModalOk">
            <b-form-group :label="$t('id')">
                <b-form-input v-model="createModalQueryId"></b-form-input>
            </b-form-group>
            <CodeEditor v-model="createModal.query.expression" :withButton="false"/>
        </main-dialog>

        <b-sidebar v-model="edit.active" right :title="edit.query.id">
            <CodeEditor v-model="edit.query.expression" :withButton="false" class="p-2"/>

            <template #footer>
                <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
                    <b-button size="sm" variant="success" @click="editQuery"> {{ $t('edit') }} </b-button>
                </div>
            </template>
        </b-sidebar>

        <b-sidebar v-model="create.active" right>
            <div class="p-2">
                <b-form-group
                    :label="$t('id')"
                >
                    <b-form-input v-model="createQueryId"></b-form-input>
                </b-form-group>
                <CodeEditor v-model="create.query.expression" :withButton="false"/>
            </div>

            <template #footer>
                <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
                    <b-button size="sm" variant="success" @click="saveQuery"> {{ $t('save') }} </b-button>
                </div>
            </template>
        </b-sidebar>
    </div>

    <b-skeleton v-else width="100%" height="250px" animation="throb"></b-skeleton>
</template>

<script>
import VSelect from 'vue-select'
import CodeEditor from '@/components/form/Editor/Code.vue'
import mysqlIcon from '@/assets/ratchet/mysql.png'
import mongoIcon from '@/assets/ratchet/mongo.png'
import pgsqlIcon from '@/assets/ratchet/pgsql.png'
import bigqueryIcon from '@/assets/ratchet/bigquery.png'

export default {
    name: "CreateRatchet",
    components: {
        VSelect,
        CodeEditor
    },
    data: function() {
        return {
            model: null,
            connectionTypes: ['MySQL', 'PostgreSQL', 'MongoDB', 'BigQuery'],
            credentialsType: ['DSN', 'Credentials'],

            edit: {
                active: false,
                query: {
                    id: '',
                    expression: null
                }
            },

            create: {
                active: false,
                query: {
                    id: '',
                    expression: null
                }
            },

            executeQuery: {
                show: false,
                query: '',
                queryName: '',
                params: [],
                namedParams: {},
                results: null
            },

            editCredentials: false,
            credentialValues: {
                dsn: '',
                user: '',
                password: '',
                host: '',
                dbname: '',
                service_account_json: ''
            },

            searchQuery: '',

            editModal: {
                show: false,
                query: {
                    id: '',
                    expression: null
                }
            },

            createModal: {
                show: false,
                query: {
                    id: '',
                    expression: null
                }
            }
        }
    },

    computed: {
        filteredQueries() {
            if (!this.model || !this.model.queries) return []
            if (!this.searchQuery.trim()) return this.model.queries
            
            const search = this.searchQuery.toLowerCase()
            return this.model.queries.filter(query => 
                query.id.toLowerCase().includes(search) ||
                (query.expression && query.expression.toLowerCase().includes(search))
            )
        },

        createModalQueryId: {
            get() {
                return this.createModal.query.id.replaceAll(" ", "_")
            },

            set(v) {
                this.createModal.query.id = v.replaceAll(" ", "_")
            }
        },
        createQueryId: {
            get() {
                return this.create.query.id.replaceAll(" ", "_")
            },

            set(v) {
                this.create.query.id = v.replaceAll(" ", "_")
            }
        },

        connectionId:{
            get() {
                return this.model.id.replaceAll(" ", "_")
            },

            set(v) {
                this.model.id = v.replaceAll(" ", "_")
            }
        },
    },

    methods: {
        getIcon(type) {
            const icons = {
                'MySQL': mysqlIcon,
                'PostgreSQL': pgsqlIcon,
                'MongoDB': mongoIcon,
                'BigQuery': bigqueryIcon
            }
            return icons[type]
        },

        openEditModal(query) {
            this.editModal.query = JSON.parse(JSON.stringify(query))
            this.editModal.show = true
        },

        handleEditModalOk(bvModalEvent) {
            bvModalEvent.preventDefault()
            this.editQueryFromModal()
        },

        async editQueryFromModal() {
            if (!this.model.queries) return
            let q = this.model.queries.find(query => query.id == this.editModal.query.id)
            if (!q) return
            q.expression = this.editModal.query.expression

            this.editModal.show = false
            this.editModal.query = {
                id: '',
                expression: null
            }

            await this.save()
        },

        handleCreateModalOk(bvModalEvent) {
            bvModalEvent.preventDefault()
            this.saveQueryFromModal()
        },

        async saveQueryFromModal() {
            let queries = this.model.queries || []

            let isDuplicate = queries.some(q => q.id === this.createModal.query.id)
            if (isDuplicate) {
                this.$bvToast.toast(this.$t('duplicateQueryId'), {
                    title: this.$t('error'),
                    variant: 'danger'
                })
                return
            }

            queries.push(this.createModal.query)

            this.createModal.show = false
            this.createModal.query = {
                id: '',
                expression: null
            }

            this.model.queries = queries

            await this.save()
        },

        editAction(query) {
            this.edit.active = true
            this.edit.query = query
        },

        async editQuery() {
            if (!this.model.queries) return
            let q = this.model.queries.find(query => query.id == this.edit.query.id)
            if (!q) return
            q.expression = this.edit.query.expression

            this.edit.active = false
            this.edit.query = {
                id: '',
                expression: null
            }

            this.save()
        },

        async removeQuery(q) {
            let confirm = await this.$mainDialog.msgBoxConfirm(`${this.$t('areYouSure')}?`)
            if (!confirm) return

            if (!this.model.queries) return
            this.model.queries = this.model.queries.filter(query => query.id != q.id)
            this.save()
        },

        newQuery() {
            this.createModal.show = true
        },

        saveQuery() {
            let queries = this.model.queries || []

            let isDuplicate = queries.some(q => q.id === this.create.query.id)
            if (isDuplicate) {
                this.$bvToast.toast(this.$t('duplicateQueryId'), {
                    title: this.$t('error'),
                    variant: 'danger'
                })
                return
            }

            queries.push(this.create.query)

            this.create.active = false
            this.create.query = {
                id: '',
                expression: null
            }

            this.model.queries = queries

            this.save()
        },

        testConnection() {
            let popup = this.$loadingPopup()
            
            this.$ratchetApi.post("/test/connection", this.model).then(() => {
                this.$bvToast.toast(this.$t('connectionEstablished'), {
                    title: this.$t('success'),
                    variant: 'success'
                })
            }).catch(err => {
                this.$bvToast.toast(err.response.data.message, {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            }).finally(() => {
                popup.hide()
            })
        },

        save() {
            let popup = this.$loadingPopup()
            
            // Create a copy of model to send
            const dataToSend = JSON.parse(JSON.stringify(this.model))
            
            // Only update credentials if edit mode was used and fields are not empty
            if (this.editCredentials) {
                let hasCredentials = false
                
                if (this.model.credentials.type === 'DSN' && this.credentialValues.dsn) {
                    dataToSend.credentials.dsn = this.credentialValues.dsn
                    hasCredentials = true
                } else if (this.model.credentials.type === 'Credentials') {
                    if (this.credentialValues.user) {
                        dataToSend.credentials.user = this.credentialValues.user
                        hasCredentials = true
                    }
                    if (this.credentialValues.password) {
                        dataToSend.credentials.password = this.credentialValues.password
                        hasCredentials = true
                    }
                    if (this.credentialValues.host) {
                        dataToSend.credentials.host = this.credentialValues.host
                        hasCredentials = true
                    }
                    if (this.credentialValues.dbname) {
                        dataToSend.credentials.dbname = this.credentialValues.dbname
                        hasCredentials = true
                    }
                } else if (this.model.credentials.type === 'Service Account JSON' && this.credentialValues.service_account_json) {
                    dataToSend.credentials.service_account_json = this.credentialValues.service_account_json
                    hasCredentials = true
                }
                
                if (!hasCredentials) {
                    popup.hide()
                    this.$bvToast.toast('Please fill in at least one credential field to update', {
                        title: this.$t('error'),
                        variant: 'warning'
                    })
                    return
                }
            }
            
            this.$ratchetApi.put("/connection", dataToSend).then(() => {
                // Reset credential values after successful save
                this.credentialValues = {
                    dsn: '',
                    user: '',
                    password: '',
                    host: '',
                    dbname: '',
                    service_account_json: ''
                }
                this.editCredentials = false
                
                this.$bvToast.toast('Connection updated successfully', {
                    title: this.$t('success'),
                    variant: 'success'
                })
            }).catch(err => {
                this.$bvToast.toast(err.response.data.message, {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            }).finally(() => {
                popup.hide()
            })
        },

        async remove() {
            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return

            let popup = this.$loadingPopup()
            this.$ratchetApi.delete("/connection/" + this.model.id)
            .then(() => {
                this.$router.push("/admin/ratchet")
            })
            .catch(err => {
                this.$bvToast.toast(err.response.data.message, {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            }).finally(() => {
                popup.hide()
            })
        },

        tryQuery(q) {
            this.executeQuery.show = true
            this.executeQuery.query = q.expression
            this.executeQuery.queryName = q.id || ''
            this.executeQuery.params = []
            this.executeQuery.namedParams = {}
            this.executeQuery.results = null
        },

        addParam() {
            this.executeQuery.params.push('')
        },

        removeParam(index) {
            this.executeQuery.params.splice(index, 1)
        },

        addNamedParam() {
            const newKey = `param_${Object.keys(this.executeQuery.namedParams).length + 1}`
            this.$set(this.executeQuery.namedParams, newKey, '')
        },

        removeNamedParam(key) {
            this.$delete(this.executeQuery.namedParams, key)
        },

        updateParamKey(oldKey, newKey) {
            if (oldKey === newKey || !newKey) return
            const value = this.executeQuery.namedParams[oldKey]
            this.$delete(this.executeQuery.namedParams, oldKey)
            this.$set(this.executeQuery.namedParams, newKey, value)
        },

        runQuery() {
            let popup = this.$loadingPopup()
            
            // Use namedParams for BigQuery, params for others
            const queryParams = this.model.type === 'BigQuery' 
                ? this.executeQuery.namedParams 
                : this.executeQuery.params

                console.log("Running query with params:", this.model)
            
            // Construct name with query name: main.{query_name}
            const queryName = this.executeQuery.queryName 
                ? `${this.model.id}.${this.executeQuery.queryName}`
                : this.model.id
            
            this.$ratchetApi.post("/query", {
                name: queryName,
                query: {
                    expression: this.executeQuery.query
                },
                params: queryParams
            }).then(resp => {
                this.executeQuery.results = resp.data
            })
            .catch(err => {
                this.$bvToast.toast(err.response.data.message, {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            }).finally(() => {
                popup.hide()
            })
        }
    },

    mounted() {
        this.$ratchetApi.get("/connection/" + this.$route.params.id).then(resp => {
            this.model = resp.data
        }).catch(err => {
            this.$bvToast.toast(err.response.data.message, {
                title: this.$t('error'),
                variant: 'danger'
            })
        })

        // ── Kortex AI tools ──────────────────────────────────────
        if (!window.kortex) return

        window.kortex.showHelper()
        window.kortex.registerContext(
            'Ratchet Connection Editor',
            'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
            'The user is editing a database connection (Ratchet). You can view the connection type and metadata, ' +
            'save configuration changes, test the connection, list defined queries, and run queries against the database. ' +
            'Use get_ratchet_info to see the current state, list_ratchet_queries to see available queries, ' +
            'run_ratchet_query to execute one, and test_ratchet_connection to verify connectivity.',
            'fa-plug'
        )

        var self = this

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'get_ratchet_info',
                    description: 'Returns the database connection metadata: type (MySQL/PostgreSQL/MongoDB/BigQuery), credential type, connection ID, and query count.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                var m = self.model
                if (!m) return { error: 'Connection not loaded yet' }
                return {
                    id: m.id,
                    type: m.type,
                    credential_type: m.credentials ? m.credentials.type : null,
                    query_count: (m.queries || []).length,
                    query_names: (m.queries || []).map(function (q) { return q.id }),
                    credentials_editing: self.editCredentials
                }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'list_ratchet_queries',
                    description: 'Lists all defined queries on this connection with their IDs.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                var m = self.model
                if (!m) return { error: 'Connection not loaded yet' }
                var queries = (m.queries || []).map(function (q) {
                    return { id: q.id, expression_preview: q.expression ? q.expression.substring(0, 200) : '' }
                })
                return { queries: queries, count: queries.length }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'run_ratchet_query',
                    description: 'Executes a named query against the database and returns the results.',
                    parameters: {
                        type: 'object',
                        required: ['query_name'],
                        properties: {
                            query_name: { type: 'string', description: 'The query ID to execute (e.g. "get_users").' }
                        }
                    }
                }
            },
            function (args) {
                var m = self.model
                if (!m) return { error: 'Connection not loaded yet' }
                var queryName = args.query_name
                var fullName = m.id + '.' + queryName
                return self.$ratchetApi.post('/query', {
                    name: fullName,
                    query: {},
                    params: []
                }).then(function (resp) {
                    return { success: true, results: resp.data, query: queryName }
                }).catch(function (err) {
                    return { success: false, error: (err.response && err.response.data && err.response.data.message) || err.message }
                })
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'test_ratchet_connection',
                    description: 'Tests the database connection and returns whether it succeeded.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                var m = self.model
                if (!m) return { error: 'Connection not loaded yet' }
                return self.$ratchetApi.post('/test/connection', m).then(function () {
                    return { success: true, message: 'Connection established successfully.' }
                }).catch(function (err) {
                    return { success: false, error: (err.response && err.response.data && err.response.data.message) || 'Connection test failed' }
                })
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'save_ratchet_connection',
                    description: 'Persists the current connection configuration to the server.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                self.save()
                return { saved: true, note: 'Save triggered. Toast notification will confirm success.' }
            }
        )
    },

    beforeDestroy() {
        if (!window.kortex) return
        window.kortex.unregisterContext()
        window.kortex.hideHelper()
        window.kortex.unregisterTool('get_ratchet_info')
        window.kortex.unregisterTool('list_ratchet_queries')
        window.kortex.unregisterTool('run_ratchet_query')
        window.kortex.unregisterTool('test_ratchet_connection')
        window.kortex.unregisterTool('save_ratchet_connection')
    }
}
</script>

<style scoped>
.action {
    cursor: pointer;
}

.action:hover {
    color: #ccc;
}

.modern-card {
    background: linear-gradient(145deg, #2a2a2a 0%, #1f1f1f 100%);
    border: 1px solid #3a3a3a;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    margin-bottom: 24px;
}

.modern-header {
    background: transparent;
    border-bottom: 1px solid #3a3a3a;
    padding: 16px 24px;
}

.modern-header h5 {
    color: var(--text-primary);
    font-weight: 600;
}

.modern-body {
    padding: 24px;
    background: transparent;
}

.card-body {
    background: transparent;
    border: none;
}

.section-subtitle {
    color: #aaa;
    font-weight: 500;
    margin-top: 20px;
    margin-bottom: 15px;
    font-size: 16px;
}

.credentials-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #3a3a3a;
}

.credential-type-badge {
    display: inline-block;
    background: linear-gradient(135deg, #6c80ff 0%, #764ba2 100%);
    color: var(--text-primary);
    padding: 8px 20px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
}

.form-label {
    color: #bbb;
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 8px;
}

.modern-input {
    border-radius: 6px;
    padding: 12px 16px;
    transition: all 0.3s ease;
}

.modern-input:focus {
    border-color: #28a745;
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.modern-input:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.modern-textarea {
    border-radius: 6px;
    padding: 12px 16px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 12px;
    transition: all 0.3s ease;
}

.modern-textarea:focus {
    border-color: #28a745;
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.modern-footer {
    background: transparent;
    border-top: 1px solid #3a3a3a;
    padding: 20px 24px;
}

.modern-btn {
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.modern-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.db-type-display {
    display: flex;
    align-items: center;
    padding: 16px;
    background: linear-gradient(145deg, #2c2c2c 0%, #242424 100%);
    border-radius: 10px;
    border: 2px solid #444;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.db-icon {
    width: 40px;
    height: 40px;
    object-fit: contain;
    margin-right: 15px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.db-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
}

.query-card {
    background: linear-gradient(145deg, #2c2c2c 0%, #242424 100%);
    border: 1px solid #3a3a3a;
    border-radius: 10px;
    padding: 20px;
    transition: all 0.3s ease;
    cursor: pointer;
}

.query-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
    border-color: #555;
}

.query-name {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 16px;
    word-break: break-word;
}

.query-actions {
    display: flex;
    gap: 8px;
}

.action-btn {
    flex: 1;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.action-btn:hover {
    transform: scale(1.05);
}

</style>